import React, { useContext, useEffect, useRef, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import { ThemeContext } from 'styled-components';

import { Typography } from '@pkg/components/DesignSystem';
import { useMediaQuery } from '@pkg/providers';

import { DRAGGABLE_CLASS_NAME_TO_IGNORE } from '@app/pages/Prototype/Grid/DraggableMasonry';

import { Counter, StyledInput, StyledTextarea, TextInputWrapper } from './styles';

export interface Props {
    className?: string;
    characterLimitClassName?: string;
    value: string | undefined;
    placeholderText?: string;
    characterLimit?: number;
    autoFocus?: boolean;
    multipleLines?: boolean;
    showBackgroundWhenHasFocus?: boolean;
    hidePlaceholderInTextArea?: boolean;
    selectAllTextOnEdit?: boolean;
    showMaskToFadeOverflow?: boolean;

    /**
     * **Note**: If `characterLimit` is set, after limit is reached this won't fire.
     */
    onChange?: (newValue: string) => void;
    onBlur?: (newUrl: string) => void;
    onEnterKeyDown?: (newUrl: string) => void;
    onFocus?: () => void;
}

const YatTextInput = ({
    className,
    characterLimitClassName,
    value = '',
    placeholderText,
    characterLimit,
    autoFocus,
    multipleLines,
    showBackgroundWhenHasFocus,
    selectAllTextOnEdit,
    showMaskToFadeOverflow,
    onChange,
    onBlur,
    onEnterKeyDown,
    onFocus,
}: Props) => {
    const theme = useContext(ThemeContext);
    const [hasFocus, setHasFocus] = useState(false);
    const [numOfCharsLeft, setNumOfCharsLeft] = useState(0);
    const valueOnFocusRef = useRef<string>();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { usingMouse } = useMediaQuery();

    useEffect(() => {
        const input = textareaRef.current || inputRef.current;
        if (!input) return;

        if (autoFocus) setTimeout(() => input.focus(), theme.transitions.speeds.fast);
    }, [autoFocus, theme]);

    useEffect(() => {
        if (!characterLimit) return;

        const charsLeft = characterLimit - value?.length;

        setNumOfCharsLeft(charsLeft);
    }, [characterLimit, value]);

    const handleFocus = () => {
        setHasFocus(true);
        if (onFocus) onFocus();

        const input = textareaRef.current || inputRef.current;
        if (!input) return;

        // Store initial value on focus
        valueOnFocusRef.current = input.value;

        if (selectAllTextOnEdit) input.select();
        else {
            input.selectionStart = input.value.length;
            input.selectionEnd = input.value.length;
        }
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        let value = event.currentTarget.value;

        // Prevent line breaks
        value = value.replace('\n', '');

        // Allow user to paste characters, but splice part over limit
        if (characterLimit) value = value.slice(0, characterLimit);

        if (onChange) onChange(value);
    };

    const handleOnBlur = () => {
        setHasFocus(false);
        if (onBlur) onBlur(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === '\n' && onEnterKeyDown) onEnterKeyDown(value);
        if (event.key === 'Enter' && onEnterKeyDown) onEnterKeyDown(value);

        // If user clicks escape key, restore previous value before focus and escape out of input
        const input = textareaRef.current || inputRef.current;
        if (event.key === 'Escape' && onChange && valueOnFocusRef.current && input) {
            onChange(valueOnFocusRef.current);
            input.blur();
        }
    };

    const updateTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        // Set to 0. This lets us find new height if row has reduced in height and is now less than before
        textarea.style.height = '0';
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    useEffect(() => {
        if (!multipleLines) return;

        window.addEventListener('resize', updateTextareaHeight);
        return () => window.removeEventListener('resize', updateTextareaHeight);
    }, [multipleLines]);

    useEffect(() => {
        if (!multipleLines) return;

        updateTextareaHeight();
    }, [multipleLines, value]);

    const sharedProps = {
        className: DRAGGABLE_CLASS_NAME_TO_IGNORE,
        value,
        placeholder: placeholderText,
        onFocus: handleFocus,
        onChange: handleOnChange,
        onBlur: handleOnBlur,
        onKeyDown: handleKeyDown,
    };

    return (
        <>
            <AnimatePresence>
                {hasFocus && characterLimit !== undefined && (
                    <Counter
                        className={characterLimitClassName}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        $usesMouse={usingMouse}
                        transition={{ duration: theme.transitions.speeds.fast }}
                        $error={numOfCharsLeft === 0}
                    >
                        <Typography type="label" size={12} weight="700">
                            {numOfCharsLeft}
                        </Typography>
                    </Counter>
                )}
            </AnimatePresence>
            <TextInputWrapper
                ref={wrapperRef}
                className={`${className} ${DRAGGABLE_CLASS_NAME_TO_IGNORE}`}
                $showBackgroundWhenHasFocus={showBackgroundWhenHasFocus}
                $hasFocus={hasFocus}
                $showMaskToFadeOverflow={
                    (hasFocus && !value && showMaskToFadeOverflow) || (!hasFocus && showMaskToFadeOverflow)
                }
            >
                {multipleLines ? (
                    <StyledTextarea ref={textareaRef} {...sharedProps} />
                ) : (
                    <StyledInput ref={inputRef} {...sharedProps} />
                )}
            </TextInputWrapper>
        </>
    );
};

export default YatTextInput;
